import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AmbientField from './components/AmbientField.jsx';
import PresentationChrome from './components/PresentationChrome.jsx';
import { slidesMeta } from './data.js';
import OpeningSlide from './slides/OpeningSlide.jsx';
import BatterySlide from './slides/BatterySlide.jsx';
import AshaSlide from './slides/AshaSlide.jsx';
import AnatomySlide from './slides/AnatomySlide.jsx';
import RahbaSlide from './slides/RahbaSlide.jsx';
import LocationSlide from './slides/LocationSlide.jsx';
import PopulationSlide from './slides/PopulationSlide.jsx';
import StatisticsSlide from './slides/StatisticsSlide.jsx';
import SchoolsSlide from './slides/SchoolsSlide.jsx';
import MarketSlide from './slides/MarketSlide.jsx';
import BranchesSlide from './slides/BranchesSlide.jsx';
import ThankYouSlide from './slides/ThankYouSlide.jsx';

const startBars = [18, 30, 46, 64, 82, 104, 82, 64, 46, 30, 18];

const getFullscreenElement = () =>
  document.fullscreenElement ||
  document.webkitFullscreenElement ||
  document.msFullscreenElement;

const slideComponents = [
  OpeningSlide,
  BatterySlide,
  AshaSlide,
  AnatomySlide,
  RahbaSlide,
  LocationSlide,
  PopulationSlide,
  StatisticsSlide,
  SchoolsSlide,
  MarketSlide,
  BranchesSlide,
  ThankYouSlide,
];

export default function App() {
  const [index, setIndex] = useState(0);
  const [presentationStarted, setPresentationStarted] = useState(false);
  const [direction, setDirection] = useState(1);
  const [fullscreen, setFullscreen] = useState(Boolean(getFullscreenElement()));
  const wheelLocked = useRef(false);
  const touchOrigin = useRef(null);
  const pointer = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const cursorRef = useRef(null);

  const go = useCallback((target) => {
    setIndex((current) => {
      const next = Math.max(0, Math.min(slidesMeta.length - 1, target));
      if (next !== current) setDirection(next > current ? 1 : -1);
      return next;
    });
  }, []);

  const next = useCallback(() => go(index + 1), [go, index]);
  const previous = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    const onKey = (event) => {
      if (!presentationStarted) return;
      if (['ArrowRight', 'ArrowDown', 'PageDown', ' '].includes(event.key)) {
        event.preventDefault();
        go(index + 1);
      }
      if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        go(index - 1);
      }
      if (event.key === 'Home') go(0);
      if (event.key === 'End') go(slidesMeta.length - 1);
      if (event.key.toLowerCase() === 'f') toggleFullscreen();
    };
    const onWheel = (event) => {
      if (!presentationStarted) return;
      if (event.target.closest?.('.scroll-guard')) return;
      if (wheelLocked.current || Math.abs(event.deltaY) < 26) return;
      wheelLocked.current = true;
      go(index + (event.deltaY > 0 ? 1 : -1));
      window.setTimeout(() => { wheelLocked.current = false; }, 760);
    };
    const onTouchStart = (event) => {
      if (!presentationStarted) return;
      const touch = event.changedTouches[0];
      touchOrigin.current = { x: touch.clientX, y: touch.clientY };
    };
    const onTouchEnd = (event) => {
      if (!presentationStarted) return;
      if (!touchOrigin.current) return;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - touchOrigin.current.x;
      const dy = touch.clientY - touchOrigin.current.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) go(index + (dx < 0 ? 1 : -1));
      touchOrigin.current = null;
    };
    const onPointer = (event) => {
      pointer.current.tx = event.clientX;
      pointer.current.ty = event.clientY;
      document.documentElement.style.setProperty('--pointer-x', `${event.clientX}px`);
      document.documentElement.style.setProperty('--pointer-y', `${event.clientY}px`);
    };
    const onFullscreen = () => setFullscreen(Boolean(getFullscreenElement()));

    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('pointermove', onPointer, { passive: true });
    document.addEventListener('fullscreenchange', onFullscreen);
    document.addEventListener('webkitfullscreenchange', onFullscreen);
    document.addEventListener('MSFullscreenChange', onFullscreen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('pointermove', onPointer);
      document.removeEventListener('fullscreenchange', onFullscreen);
      document.removeEventListener('webkitfullscreenchange', onFullscreen);
      document.removeEventListener('MSFullscreenChange', onFullscreen);
    };
  }, [go, index, presentationStarted]);

  useEffect(() => {
    let frame = 0;
    const loop = () => {
      pointer.current.x += (pointer.current.tx - pointer.current.x) * .16;
      pointer.current.y += (pointer.current.ty - pointer.current.y) * .16;
      if (cursorRef.current) {
        cursorRef.current.style.transform = `translate3d(${pointer.current.x}px, ${pointer.current.y}px, 0)`;
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!getFullscreenElement()) {
      const root = document.documentElement;
      const requestFullscreen =
        root.requestFullscreen ||
        root.webkitRequestFullscreen ||
        root.msRequestFullscreen;
      requestFullscreen?.call(root);
      return;
    }

    const exitFullscreen =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;
    exitFullscreen?.call(document);
  }, []);

  const startPresentation = useCallback(async () => {
    go(0);

    const root = document.documentElement;
    const requestFullscreen =
      root.requestFullscreen ||
      root.webkitRequestFullscreen ||
      root.msRequestFullscreen;

    try {
      if (!getFullscreenElement() && requestFullscreen) {
        await requestFullscreen.call(root);
      }
    } catch (error) {
      console.warn('Fullscreen could not be started automatically after the click.', error);
    } finally {
      setPresentationStarted(true);
    }
  }, [go]);

  const currentMeta = slidesMeta[index];
  const experienceClass = useMemo(
    () => `experience tone-${currentMeta.tone} direction-${direction > 0 ? 'forward' : 'backward'}`,
    [currentMeta.tone, direction],
  );

  return (
    <main className={experienceClass} data-scene={index}>
      {!presentationStarted && (
        <button
          className="presentation-start-screen"
          type="button"
          onClick={startPresentation}
          aria-label="Start presentation in full screen"
        >
          <div className="presentation-start-grid" aria-hidden="true" />
          <div className="presentation-start-rings" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>

          <div className="presentation-start-content">
            <img
              className="presentation-start-logo"
              src="/assets/logo-beige.svg"
              alt="Touch of Health Rehabilitation Center"
            />

            <div className="presentation-start-bars" aria-hidden="true">
              {startBars.map((height, barIndex) => (
                <i
                  key={`${height}-${barIndex}`}
                  style={{ '--start-bar-height': `${height}px`, '--start-bar-delay': `${barIndex * -0.08}s` }}
                />
              ))}
            </div>

            <span className="presentation-start-kicker">Immersive Audiology Presentation</span>
            <strong>Start Presentation</strong>
            <small>Click once to enter full screen</small>
          </div>

          <svg
            className="presentation-start-wave"
            viewBox="0 0 1600 260"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M-40 130 C70 130 90 55 180 130 S315 205 410 130 S560 45 680 130 S850 215 980 130 S1150 55 1270 130 S1435 195 1640 92" />
            <path d="M-40 130 C90 130 115 92 205 130 S340 168 435 130 S585 88 700 130 S865 172 995 130 S1170 92 1290 130 S1455 160 1640 120" />
          </svg>

          <span className="presentation-start-note">Press Esc anytime to leave full screen</span>
        </button>
      )}
      <AmbientField tone={currentMeta.tone} variant={currentMeta.ambient} />
      <div className="ambient-grid" aria-hidden="true" />
      <div className="cursor-ring" ref={cursorRef} aria-hidden="true" />
      <PresentationChrome
        slides={slidesMeta}
        index={index}
        tone={currentMeta.tone}
        onGo={go}
        onPrevious={previous}
        onNext={next}
        fullscreen={fullscreen}
        onFullscreen={toggleFullscreen}
      />

      <div className="slides-stage">
        {slideComponents.map((SlideComponent, slideIndex) => {
          const stateClass = slideIndex === index
            ? 'is-active'
            : slideIndex < index
              ? 'is-past'
              : 'is-next';
          return (
            <div
              className={`slide-shell ${stateClass}`}
              data-tone={slidesMeta[slideIndex].tone}
              data-slide={slideIndex + 1}
              key={slidesMeta[slideIndex].label}
              aria-hidden={slideIndex !== index}
            >
              <SlideComponent active={slideIndex === index} />
            </div>
          );
        })}
      </div>
    </main>
  );
}
